import React, { useEffect, useState, useRef, useCallback } from "react";
import atlas from "../Assets/mini-atlas.png";

/* A scale figure, the way an architectural drawing drops a tiny
   human in to show proportion. This one has a life of its own.

   He turns up rarely, performs one short act, and leaves. The atlas
   is 1056x800: eight rows-worth of cells at 132x160, drawn at half
   size so it stays sharp on 2x displays.

   Movement lives on the wrapper, the frame cycle on the sprite, so
   the two never fight over a transform. */

const ROWS = {
  run: { row: 0, cycle: "run" },
  idle: { row: 1, cycle: "still" }, // arms crossed
  stand: { row: 2, cycle: "still" }, // hands in pockets
  laptop: { row: 3, cycle: "still" }, // the three laptop cells are
  // different camera angles rather than animation frames, so this
  // one holds on the first cell instead of cycling through them.
  wave: { row: 4, cycle: "wave" },
};

/* Positions are given as a fraction of the crossing, 0 at the edge
   he enters from and 1 at the edge he leaves by, so an act reads the
   same in either direction. */
const ACTS = [
  // Straight through, no stopping.
  [{ clip: "run", to: 1, ms: 8200 }],

  // Jogs in, notices you, waves, carries on.
  [
    { clip: "run", to: 0.34, ms: 3100 },
    { clip: "wave", hold: 2400 },
    { clip: "run", to: 1, ms: 4300 },
  ],

  // Stops to get some work done.
  [
    { clip: "run", to: 0.26, ms: 2600 },
    { clip: "laptop", hold: 6800 },
    { clip: "run", to: 1, ms: 4800 },
  ],

  // Pulls up, has a look around, moves on.
  [
    { clip: "run", to: 0.46, ms: 3400 },
    { clip: "idle", hold: 2500 },
    { clip: "run", to: 1, ms: 4200 },
  ],

  // Same, in the other posture.
  [
    { clip: "run", to: 0.6, ms: 4200 },
    { clip: "stand", hold: 2200 },
    { clip: "run", to: 1, ms: 3600 },
  ],
];

const FIRST_WAIT = 13000;
const GAP_MIN = 58000;
const GAP_MAX = 104000;

const rand = (a, z) => a + Math.random() * (z - a);
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

/* Off-screen either side, so he enters and leaves cleanly. */
const posFor = (dir, t) => (dir === 1 ? -12 + t * 124 : 112 - t * 124);

const MiniMe = () => {
  const [show, setShow] = useState(null); // { act, dir }
  const [step, setStep] = useState(0);
  const [started, setStarted] = useState(false);

  const timer = useRef(null);
  const stepTimer = useRef(null);

  const schedule = useCallback((delay) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (document.visibilityState !== "visible") {
        schedule(9000);
        return;
      }
      setStep(0);
      setStarted(false);
      setShow({ act: pick(ACTS), dir: Math.random() < 0.5 ? 1 : -1 });
    }, delay);
  }, []);

  useEffect(() => {
    const reduced =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return undefined;

    schedule(FIRST_WAIT);
    return () => {
      clearTimeout(timer.current);
      clearTimeout(stepTimer.current);
    };
  }, [schedule]);

  // Paint once at the entry position, then begin: without this the
  // first move would animate from wherever the browser assumed.
  useEffect(() => {
    if (!show || started) return undefined;
    const raf = requestAnimationFrame(() => setStarted(true));
    return () => cancelAnimationFrame(raf);
  }, [show, started]);

  // Advance through the act.
  useEffect(() => {
    if (!show || !started) return undefined;

    const current = show.act[step];
    if (!current) return undefined;

    const dwell = current.hold ?? current.ms;
    stepTimer.current = setTimeout(() => {
      if (step + 1 < show.act.length) {
        setStep(step + 1);
      } else {
        setShow(null);
        schedule(rand(GAP_MIN, GAP_MAX));
      }
    }, dwell);

    return () => clearTimeout(stepTimer.current);
  }, [show, started, step, schedule]);

  if (!show) return null;

  const { act, dir } = show;
  const current = act[step];
  const meta = ROWS[current.clip];

  // Where he should be for this step, and how long to take getting there.
  let travelled = 0;
  for (let i = 0; i < step; i += 1) travelled = act[i].to ?? travelled;
  const target = current.to ?? travelled;
  const x = started ? posFor(dir, target) : posFor(dir, 0);
  const moveMs = started && current.to !== undefined ? current.ms : 0;

  return (
    <div
      className="minime"
      style={{
        transform: `translateX(${x}vw)`,
        transition: moveMs ? `transform ${moveMs}ms linear` : "none",
      }}
      aria-hidden="true"
    >
      <div
        className={`minime-sprite mm-${meta.cycle}`}
        style={{
          backgroundImage: `url(${atlas})`,
          backgroundPositionY: `${-meta.row * 80}px`,
          "--dir": dir,
        }}
      />
    </div>
  );
};

export default MiniMe;
